/**
 * Mock API Service
 * 
 * This service provides a localStorage-based mock API that mimics the real API structure.
 * When the backend is ready, simply replace the imports from 'mockApi' to 'api' in components.
 * 
 * All data is stored in localStorage with the same keys as the prototype:
 * - organizations
 * - globalPrograms
 * - orgPrograms
 * - globalRoles
 * - globalUsers
 * - groups
 * - deviceTypes
 * - deviceOrders
 */

import { storage } from '@/utils/storage';

// Helper to create a delay (simulate network latency)
const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

// Helper to create API response format
const createResponse = <T>(data: T, success = true) => ({
  success,
  data,
  error: null,
});

// Helper to create paginated response
const createPaginatedResponse = <T>(
  data: T[],
  page: number = 1,
  limit: number = 100,
  total?: number
) => ({
  data,
  total: total ?? data.length,
  page,
  limit,
});

// Mock API instance (mimics axios structure)
export const mockApi = {
  get: async <T = any>(url: string, config?: { params?: any }): Promise<{ data: T }> => {
    await delay(300); // Simulate network delay
    
    const [endpoint, id] = url.split('/').filter(Boolean);
    
    switch (endpoint) {
      case 'organization':
        return handleOrganizationGet(id, config?.params) as Promise<{ data: T }>;
      case 'program':
        return handleProgramGet(id, config?.params) as Promise<{ data: T }>;
      case 'role':
        return handleRoleGet(id, config?.params) as Promise<{ data: T }>;
      case 'user':
        return handleUserGet(id, config?.params) as Promise<{ data: T }>;
      case 'device':
        return handleDeviceGet(id, config?.params) as Promise<{ data: T }>;
      case 'device-order':
        return handleDeviceOrderGet(id, config?.params) as Promise<{ data: T }>;
      case 'patient':
        return handlePatientGet(id, config?.params) as Promise<{ data: T }>;
      case 'measurement':
        return handleMeasurementGet(id, config?.params) as Promise<{ data: T }>;
      case 'task-result':
        return handleTaskResultGet(id, config?.params) as Promise<{ data: T }>;
      case 'encounter':
        return handleEncounterGet(id, config?.params) as Promise<{ data: T }>;
      case 'follower':
        return handleFollowerGet(id, config?.params) as Promise<{ data: T }>;
      case 'group':
        return handleGroupGet(id, config?.params) as Promise<{ data: T }>;
      default:
        throw new Error(`Unknown endpoint: ${endpoint}`);
    }
  },

  post: async <T = any>(url: string, data?: any): Promise<{ data: T }> => {
    await delay(300);
    
    const [endpoint] = url.split('/').filter(Boolean);
    
    switch (endpoint) {
      case 'organization':
        return handleOrganizationPost(data) as Promise<{ data: T }>;
      case 'program':
        return handleProgramPost(data) as Promise<{ data: T }>;
      case 'role':
        return handleRolePost(data) as Promise<{ data: T }>;
      case 'user':
        return handleUserPost(data) as Promise<{ data: T }>;
      case 'device':
        return handleDevicePost(data) as Promise<{ data: T }>;
      case 'device-order':
        return handleDeviceOrderPost(data) as Promise<{ data: T }>;
      case 'patient':
        return handlePatientPost(data) as Promise<{ data: T }>;
      case 'measurement':
        return handleMeasurementPost(data) as Promise<{ data: T }>;
      case 'task-result':
        return handleTaskResultPost(data) as Promise<{ data: T }>;
      case 'encounter':
        return handleEncounterPost(data) as Promise<{ data: T }>;
      case 'follower':
        return handleFollowerPost(data) as Promise<{ data: T }>;
      case 'group':
        return handleGroupPost(data) as Promise<{ data: T }>;
      default:
        throw new Error(`Unknown endpoint: ${endpoint}`);
    }
  },

  put: async <T = any>(url: string, data?: any): Promise<{ data: T }> => {
    await delay(300);
    
    const [endpoint, id] = url.split('/').filter(Boolean);
    
    if (!id) {
      throw new Error('ID is required for PUT requests');
    }
    
    switch (endpoint) {
      case 'organization':
        return handleOrganizationPut(id, data) as Promise<{ data: T }>;
      case 'program':
        return handleProgramPut(id, data) as Promise<{ data: T }>;
      case 'role':
        return handleRolePut(id, data) as Promise<{ data: T }>;
      case 'user':
        return handleUserPut(id, data) as Promise<{ data: T }>;
      case 'device':
        return handleDevicePut(id, data) as Promise<{ data: T }>;
      case 'device-order':
        return handleDeviceOrderPut(id, data) as Promise<{ data: T }>;
      case 'patient':
        return handlePatientPut(id, data) as Promise<{ data: T }>;
      case 'measurement':
        return handleMeasurementPut(id, data) as Promise<{ data: T }>;
      case 'task-result':
        return handleTaskResultPut(id, data) as Promise<{ data: T }>;
      case 'encounter':
        return handleEncounterPut(id, data) as Promise<{ data: T }>;
      case 'follower':
        return handleFollowerPut(id, data) as Promise<{ data: T }>;
      case 'group':
        return handleGroupPut(id, data) as Promise<{ data: T }>;
      default:
        throw new Error(`Unknown endpoint: ${endpoint}`);
    }
  },

  delete: async <T = any>(url: string): Promise<{ data: T }> => {
    await delay(300);
    
    const [endpoint, id] = url.split('/').filter(Boolean);
    
    if (!id) {
      throw new Error('ID is required for DELETE requests');
    }
    
    switch (endpoint) {
      case 'organization':
        return handleOrganizationDelete(id) as Promise<{ data: T }>;
      case 'program':
        return handleProgramDelete(id) as Promise<{ data: T }>;
      case 'role':
        return handleRoleDelete(id) as Promise<{ data: T }>;
      case 'user':
        return handleUserDelete(id) as Promise<{ data: T }>;
      case 'device':
        return handleDeviceDelete(id) as Promise<{ data: T }>;
      case 'device-order':
        return handleDeviceOrderDelete(id) as Promise<{ data: T }>;
      case 'patient':
        return handlePatientDelete(id) as Promise<{ data: T }>;
      case 'measurement':
        return handleMeasurementDelete(id) as Promise<{ data: T }>;
      case 'task-result':
        return handleTaskResultDelete(id) as Promise<{ data: T }>;
      case 'encounter':
        return handleEncounterDelete(id) as Promise<{ data: T }>;
      case 'follower':
        return handleFollowerDelete(id) as Promise<{ data: T }>;
      case 'group':
        return handleGroupDelete(id) as Promise<{ data: T }>;
      default:
        throw new Error(`Unknown endpoint: ${endpoint}`);
    }
  },
};

// ============================================================================
// Organization Handlers
// ============================================================================

function handleOrganizationGet(id: string | undefined, params?: any) {
  const organizations = storage.get<any[]>('organizations', []);
  
  if (id) {
    const org = organizations.find(o => o.org_uuid === id || o.id === id);
    if (!org) {
      throw { response: { status: 404, data: { error: { message: 'Organization not found' } } } };
    }
    return { data: createResponse(org) };
  }
  
  // List organizations with pagination
  const page = params?.page || 1;
  const limit = params?.limit || 100;
  const name = params?.name?.toLowerCase();
  const includeDeleted = params?.includeDeleted;
  
  let filtered = organizations;
  
  // Filter by name if provided
  if (name) {
    filtered = filtered.filter(o => o.name?.toLowerCase().includes(name));
  }
  
  // Filter out deleted unless includeDeleted is true
  if (!includeDeleted) {
    filtered = filtered.filter(o => o.status !== 'deleted' && o.deletedAt === undefined);
  }
  
  const start = (page - 1) * limit;
  const end = start + limit;
  const paginated = filtered.slice(start, end);
  
  return {
    data: createResponse(createPaginatedResponse(paginated, page, limit, filtered.length)),
  };
}

function handleOrganizationPost(data: any) {
  const organizations = storage.get<any[]>('organizations', []);
  
  const newOrg = {
    org_uuid: `org_${Date.now()}`,
    id: `org_${Date.now()}`,
    ...data,
    parentId: data.parentId || data.parent_id || null,
    programs: data.programs || [],
    roles: data.roles || [],
    users: data.users || [],
    features: data.features || [],
    inheritedFeatures: [],
    createdAt: new Date().toISOString(),
    modifiedAt: new Date().toISOString(),
    subOrgs: 0,
  };
  
  organizations.push(newOrg);
  storage.set('organizations', organizations);
  
  return { data: createResponse(newOrg) };
}

function handleOrganizationPut(id: string, data: any) {
  const organizations = storage.get<any[]>('organizations', []);
  const index = organizations.findIndex(o => o.org_uuid === id || o.id === id);
  
  if (index === -1) {
    throw { response: { status: 404, data: { error: { message: 'Organization not found' } } } };
  }
  
  organizations[index] = {
    ...organizations[index],
    ...data,
    modifiedAt: new Date().toISOString(),
  };
  
  storage.set('organizations', organizations);
  
  return { data: createResponse(organizations[index]) };
}

function handleOrganizationDelete(id: string) {
  const organizations = storage.get<any[]>('organizations', []);
  const filtered = organizations.filter(o => o.org_uuid !== id && o.id !== id);
  
  if (filtered.length === organizations.length) {
    throw { response: { status: 404, data: { error: { message: 'Organization not found' } } } };
  }
  
  storage.set('organizations', filtered);
  
  return { data: createResponse({ success: true }) };
}

// ============================================================================
// Program Handlers (stub - will be implemented)
// ============================================================================

function handleProgramGet(id: string | undefined, params?: any) {
  const globalPrograms = storage.get<any[]>('globalPrograms', []);
  const orgPrograms = storage.get<any[]>('orgPrograms', []);
  
  if (id) {
    const program = [...globalPrograms, ...orgPrograms].find(p => p.id === id);
    if (!program) {
      throw { response: { status: 404, data: { error: { message: 'Program not found' } } } };
    }
    return { data: createResponse(program) };
  }
  
  // Filter by type if provided
  let programs: any[] = [];
  if (params?.type === 'global') {
    programs = globalPrograms;
  } else if (params?.type === 'organization') {
    programs = orgPrograms;
    // Filter by organizationId if provided
    if (params?.organizationId) {
      programs = programs.filter(p => p.organizationId === params.organizationId);
    }
  } else {
    // If no type specified, return all programs
    programs = [...globalPrograms, ...orgPrograms];
  }
  
  // Filter by status if provided
  if (params?.status) {
    programs = programs.filter(p => p.status === params.status);
  }
  
  // Filter out deleted programs unless explicitly requested
  if (!params?.includeDeleted) {
    programs = programs.filter(p => p.status !== 'deleted');
  }
  
  const page = params?.page || 1;
  const limit = params?.limit || 100;
  
  const start = (page - 1) * limit;
  const end = start + limit;
  const paginated = programs.slice(start, end);
  
  return {
    data: createResponse(createPaginatedResponse(paginated, page, limit, programs.length)),
  };
}

function handleProgramPost(data: any) {
  const globalPrograms = storage.get<any[]>('globalPrograms', []);
  const orgPrograms = storage.get<any[]>('orgPrograms', []);
  
  const newProgram = {
    id: `program_${Date.now()}`,
    ...data,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
  
  if (data.type === 'global') {
    globalPrograms.push(newProgram);
    storage.set('globalPrograms', globalPrograms);
  } else {
    orgPrograms.push(newProgram);
    storage.set('orgPrograms', orgPrograms);
  }
  
  return { data: createResponse(newProgram) };
}

function handleProgramPut(id: string, data: any) {
  const globalPrograms = storage.get<any[]>('globalPrograms', []);
  const orgPrograms = storage.get<any[]>('orgPrograms', []);
  
  let program = globalPrograms.find(p => p.id === id);
  let isGlobal = true;
  
  if (!program) {
    program = orgPrograms.find(p => p.id === id);
    isGlobal = false;
  }
  
  if (!program) {
    throw { response: { status: 404, data: { error: { message: 'Program not found' } } } };
  }
  
  const updated = { ...program, ...data, updatedAt: new Date().toISOString() };
  
  if (isGlobal) {
    const index = globalPrograms.findIndex(p => p.id === id);
    globalPrograms[index] = updated;
    storage.set('globalPrograms', globalPrograms);
  } else {
    const index = orgPrograms.findIndex(p => p.id === id);
    orgPrograms[index] = updated;
    storage.set('orgPrograms', orgPrograms);
  }
  
  return { data: createResponse(updated) };
}

function handleProgramDelete(id: string) {
  const globalPrograms = storage.get<any[]>('globalPrograms', []);
  const orgPrograms = storage.get<any[]>('orgPrograms', []);
  
  const globalIndex = globalPrograms.findIndex(p => p.id === id);
  const orgIndex = orgPrograms.findIndex(p => p.id === id);
  
  if (globalIndex === -1 && orgIndex === -1) {
    throw { response: { status: 404, data: { error: { message: 'Program not found' } } } };
  }
  
  if (globalIndex !== -1) {
    globalPrograms.splice(globalIndex, 1);
    storage.set('globalPrograms', globalPrograms);
  }
  
  if (orgIndex !== -1) {
    orgPrograms.splice(orgIndex, 1);
    storage.set('orgPrograms', orgPrograms);
  }
  
  return { data: createResponse({ success: true }) };
}

// ============================================================================
// Role Handlers (stub - will be implemented)
// ============================================================================

function handleRoleGet(id: string | undefined, params?: any) {
  const globalRoles = storage.get<any[]>('globalRoles', []);
  
  if (id) {
    const role = globalRoles.find(r => r.id === id);
    if (!role) {
      throw { response: { status: 404, data: { error: { message: 'Role not found' } } } };
    }
    return { data: createResponse(role) };
  }
  
  const page = params?.page || 1;
  const limit = params?.limit || 100;
  const start = (page - 1) * limit;
  const end = start + limit;
  const paginated = globalRoles.slice(start, end);
  
  return {
    data: createResponse(createPaginatedResponse(paginated, page, limit, globalRoles.length)),
  };
}

function handleRolePost(data: any) {
  const globalRoles = storage.get<any[]>('globalRoles', []);
  
  const newRole = {
    id: `role_${Date.now()}`,
    ...data,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
  
  globalRoles.push(newRole);
  storage.set('globalRoles', globalRoles);
  
  return { data: createResponse(newRole) };
}

function handleRolePut(id: string, data: any) {
  const globalRoles = storage.get<any[]>('globalRoles', []);
  const index = globalRoles.findIndex(r => r.id === id);
  
  if (index === -1) {
    throw { response: { status: 404, data: { error: { message: 'Role not found' } } } };
  }
  
  globalRoles[index] = {
    ...globalRoles[index],
    ...data,
    updatedAt: new Date().toISOString(),
  };
  
  storage.set('globalRoles', globalRoles);
  
  return { data: createResponse(globalRoles[index]) };
}

function handleRoleDelete(id: string) {
  const globalRoles = storage.get<any[]>('globalRoles', []);
  const filtered = globalRoles.filter(r => r.id !== id);
  
  if (filtered.length === globalRoles.length) {
    throw { response: { status: 404, data: { error: { message: 'Role not found' } } } };
  }
  
  storage.set('globalRoles', filtered);
  
  return { data: createResponse({ success: true }) };
}

// ============================================================================
// User Handlers (stub - will be implemented)
// ============================================================================

function handleUserGet(id: string | undefined, params?: any) {
  const globalUsers = storage.get<any[]>('globalUsers', []);
  
  if (id) {
    const user = globalUsers.find(u => u.id === id);
    if (!user) {
      throw { response: { status: 404, data: { error: { message: 'User not found' } } } };
    }
    return { data: createResponse(user) };
  }
  
  const page = params?.page || 1;
  const limit = params?.limit || 100;
  const start = (page - 1) * limit;
  const end = start + limit;
  const paginated = globalUsers.slice(start, end);
  
  return {
    data: createResponse(createPaginatedResponse(paginated, page, limit, globalUsers.length)),
  };
}

function handleUserPost(data: any) {
  const globalUsers = storage.get<any[]>('globalUsers', []);
  
  const newUser = {
    id: `user_${Date.now()}`,
    ...data,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
  
  globalUsers.push(newUser);
  storage.set('globalUsers', globalUsers);
  
  return { data: createResponse(newUser) };
}

function handleUserPut(id: string, data: any) {
  const globalUsers = storage.get<any[]>('globalUsers', []);
  const index = globalUsers.findIndex(u => u.id === id);
  
  if (index === -1) {
    throw { response: { status: 404, data: { error: { message: 'User not found' } } } };
  }
  
  globalUsers[index] = {
    ...globalUsers[index],
    ...data,
    updatedAt: new Date().toISOString(),
  };
  
  storage.set('globalUsers', globalUsers);
  
  return { data: createResponse(globalUsers[index]) };
}

function handleUserDelete(id: string) {
  const globalUsers = storage.get<any[]>('globalUsers', []);
  const filtered = globalUsers.filter(u => u.id !== id);
  
  if (filtered.length === globalUsers.length) {
    throw { response: { status: 404, data: { error: { message: 'User not found' } } } };
  }
  
  storage.set('globalUsers', filtered);
  
  return { data: createResponse({ success: true }) };
}

// ============================================================================
// Device Handlers (stub - will be implemented)
// ============================================================================

function handleDeviceGet(id: string | undefined, params?: any) {
  const deviceTypes = storage.get<any[]>('deviceTypes', []);
  
  if (id) {
    const device = deviceTypes.find(d => d.id === id);
    if (!device) {
      throw { response: { status: 404, data: { error: { message: 'Device not found' } } } };
    }
    return { data: createResponse(device) };
  }
  
  const page = params?.page || 1;
  const limit = params?.limit || 100;
  const start = (page - 1) * limit;
  const end = start + limit;
  const paginated = deviceTypes.slice(start, end);
  
  return {
    data: createResponse(createPaginatedResponse(paginated, page, limit, deviceTypes.length)),
  };
}

function handleDevicePost(data: any) {
  const deviceTypes = storage.get<any[]>('deviceTypes', []);
  
  const newDevice = {
    id: `device_${Date.now()}`,
    ...data,
    createdAt: new Date().toISOString(),
  };
  
  deviceTypes.push(newDevice);
  storage.set('deviceTypes', deviceTypes);
  
  return { data: createResponse(newDevice) };
}

function handleDevicePut(id: string, data: any) {
  const deviceTypes = storage.get<any[]>('deviceTypes', []);
  const index = deviceTypes.findIndex(d => d.id === id);
  
  if (index === -1) {
    throw { response: { status: 404, data: { error: { message: 'Device not found' } } } };
  }
  
  deviceTypes[index] = {
    ...deviceTypes[index],
    ...data,
  };
  
  storage.set('deviceTypes', deviceTypes);
  
  return { data: createResponse(deviceTypes[index]) };
}

function handleDeviceDelete(id: string) {
  const deviceTypes = storage.get<any[]>('deviceTypes', []);
  const filtered = deviceTypes.filter(d => d.id !== id);
  
  if (filtered.length === deviceTypes.length) {
    throw { response: { status: 404, data: { error: { message: 'Device not found' } } } };
  }
  
  storage.set('deviceTypes', filtered);
  
  return { data: createResponse({ success: true }) };
}

// ============================================================================
// Device Order Handlers (stub - will be implemented)
// ============================================================================

function handleDeviceOrderGet(id: string | undefined, params?: any) {
  let deviceOrders = storage.get<any[]>('deviceOrders', []);
  
  // Filter by patientId if provided
  if (params?.patientId) {
    deviceOrders = deviceOrders.filter(o => o.patientId === String(params.patientId));
  }
  
  // Filter by status if provided
  if (params?.status) {
    deviceOrders = deviceOrders.filter(o => o.status === params.status);
  }
  
  if (id) {
    const order = deviceOrders.find(o => o.id === id);
    if (!order) {
      throw { response: { status: 404, data: { error: { message: 'Device order not found' } } } };
    }
    return { data: createResponse(order) };
  }
  
  const page = params?.page || 1;
  const limit = params?.limit || 100;
  const start = (page - 1) * limit;
  const end = start + limit;
  const paginated = deviceOrders.slice(start, end);
  
  return {
    data: createResponse(createPaginatedResponse(paginated, page, limit, deviceOrders.length)),
  };
}

function handleDeviceOrderPost(data: any) {
  const deviceOrders = storage.get<any[]>('deviceOrders', []);
  
  const newOrder = {
    id: `order_${Date.now()}`,
    ...data,
    createdAt: new Date().toISOString(),
    status: data.status || 'pending',
  };
  
  deviceOrders.push(newOrder);
  storage.set('deviceOrders', deviceOrders);
  
  return { data: createResponse(newOrder) };
}

function handleDeviceOrderPut(id: string, data: any) {
  const deviceOrders = storage.get<any[]>('deviceOrders', []);
  const index = deviceOrders.findIndex(o => o.id === id);
  
  if (index === -1) {
    throw { response: { status: 404, data: { error: { message: 'Device order not found' } } } };
  }
  
  deviceOrders[index] = {
    ...deviceOrders[index],
    ...data,
  };
  
  storage.set('deviceOrders', deviceOrders);
  
  return { data: createResponse(deviceOrders[index]) };
}

function handleDeviceOrderDelete(id: string) {
  const deviceOrders = storage.get<any[]>('deviceOrders', []);
  const filtered = deviceOrders.filter(o => o.id !== id);
  
  if (filtered.length === deviceOrders.length) {
    throw { response: { status: 404, data: { error: { message: 'Device order not found' } } } };
  }
  
  storage.set('deviceOrders', filtered);
  
  return { data: createResponse({ success: true }) };
}

// ============================================================================
// Patient Handlers
// ============================================================================

async function handlePatientGet(id: string | undefined, params?: any) {
  const patients = storage.get<any[]>('pp_data_v1', []);
  
  if (id) {
    const patient = patients.find(p => p.id === id || p.id === parseInt(id));
    if (!patient) {
      throw { response: { status: 404, data: { error: { message: 'Patient not found' } } } };
    }
    return { data: createResponse(patient) };
  }
  
  // List patients with filtering, sorting, and pagination
  let filtered = [...patients];
  
  // Apply filters
  if (params?.search) {
    const searchLower = params.search.toLowerCase();
    filtered = filtered.filter(p => {
      const searchable = [
        p.firstName,
        p.lastName,
        p.mrn,
        p.mobile,
        p.email,
      ].filter(Boolean).join(' ').toLowerCase();
      return searchable.includes(searchLower);
    });
  }
  
  if (params?.firstName) {
    filtered = filtered.filter(p => 
      p.firstName?.toLowerCase().includes(params.firstName.toLowerCase())
    );
  }
  
  if (params?.lastName) {
    filtered = filtered.filter(p => 
      p.lastName?.toLowerCase().includes(params.lastName.toLowerCase())
    );
  }
  
  if (params?.mrn) {
    filtered = filtered.filter(p => 
      p.mrn?.toLowerCase().includes(params.mrn.toLowerCase())
    );
  }
  
  // Apply sorting
  if (params?.sortBy) {
    const direction = params.sortDirection === 'desc' ? -1 : 1;
    filtered.sort((a, b) => {
      const aVal = a[params.sortBy] || '';
      const bVal = b[params.sortBy] || '';
      if (aVal < bVal) return -1 * direction;
      if (aVal > bVal) return 1 * direction;
      return 0;
    });
  }
  
  // Apply pagination
  const page = params?.page || 1;
  const limit = params?.limit || params?.pageSize || 25;
  const start = (page - 1) * limit;
  const end = start + limit;
  const paginated = filtered.slice(start, end);
  
  return {
    data: createResponse(createPaginatedResponse(paginated, page, limit, filtered.length)),
  };
}

async function handlePatientPost(data: any) {
  const patients = storage.get<any[]>('pp_data_v1', []);
  
  const newPatient = {
    id: `patient_${Date.now()}`,
    ...data,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
  
  patients.push(newPatient);
  storage.set('pp_data_v1', patients);
  
  return { data: createResponse(newPatient) };
}

async function handlePatientPut(id: string, data: any) {
  const patients = storage.get<any[]>('pp_data_v1', []);
  const index = patients.findIndex(p => p.id === id || p.id === parseInt(id));
  
  if (index === -1) {
    throw { response: { status: 404, data: { error: { message: 'Patient not found' } } } };
  }
  
  patients[index] = {
    ...patients[index],
    ...data,
    updatedAt: new Date().toISOString(),
  };
  
  storage.set('pp_data_v1', patients);
  
  return { data: createResponse(patients[index]) };
}

async function handlePatientDelete(id: string) {
  const patients = storage.get<any[]>('pp_data_v1', []);
  const filtered = patients.filter(p => p.id !== id && p.id !== parseInt(id));
  
  if (filtered.length === patients.length) {
    throw { response: { status: 404, data: { error: { message: 'Patient not found' } } } };
  }
  
  storage.set('pp_data_v1', filtered);
  
  return { data: createResponse({ success: true }) };
}

// ============================================================================
// Measurement Handlers
// ============================================================================

async function handleMeasurementGet(id: string | undefined, params?: any) {
  const measurements = storage.get<any[]>('pp_measurements_v1', []);
  
  if (id) {
    const measurement = measurements.find(m => m.id === id);
    if (!measurement) {
      throw { response: { status: 404, data: { error: { message: 'Measurement not found' } } } };
    }
    return { data: createResponse(measurement) };
  }
  
  // List measurements with filtering
  let filtered = [...measurements];
  
  // Filter by patientId
  if (params?.patientId) {
    filtered = filtered.filter(m => m.patientId === params.patientId || String(m.patientId) === String(params.patientId));
  }
  
  // Filter by type
  if (params?.type) {
    filtered = filtered.filter(m => m.type === params.type);
  }
  
  // Filter by date range
  if (params?.startDate) {
    filtered = filtered.filter(m => new Date(m.timestamp) >= new Date(params.startDate));
  }
  if (params?.endDate) {
    filtered = filtered.filter(m => new Date(m.timestamp) <= new Date(params.endDate));
  }
  
  // Sort by timestamp (newest first)
  filtered.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  
  return { data: createResponse(filtered) };
}

async function handleMeasurementPost(data: any) {
  const measurements = storage.get<any[]>('pp_measurements_v1', []);
  
  const newMeasurement = {
    id: `measurement_${Date.now()}`,
    ...data,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
  
  measurements.push(newMeasurement);
  storage.set('pp_measurements_v1', measurements);
  
  return { data: createResponse(newMeasurement) };
}

async function handleMeasurementPut(id: string, data: any) {
  const measurements = storage.get<any[]>('pp_measurements_v1', []);
  const index = measurements.findIndex(m => m.id === id);
  
  if (index === -1) {
    throw { response: { status: 404, data: { error: { message: 'Measurement not found' } } } };
  }
  
  measurements[index] = {
    ...measurements[index],
    ...data,
    updatedAt: new Date().toISOString(),
  };
  
  storage.set('pp_measurements_v1', measurements);
  
  return { data: createResponse(measurements[index]) };
}

async function handleMeasurementDelete(id: string) {
  const measurements = storage.get<any[]>('pp_measurements_v1', []);
  const filtered = measurements.filter(m => m.id !== id);
  
  if (filtered.length === measurements.length) {
    throw { response: { status: 404, data: { error: { message: 'Measurement not found' } } } };
  }
  
  storage.set('pp_measurements_v1', filtered);
  
  return { data: createResponse({ success: true }) };
}

// Task Result Handlers
async function handleTaskResultGet(id: string | undefined, params?: any) {
  const taskResults = storage.get<any[]>('pp_task_results_v1', []);
  
  if (id) {
    const taskResult = taskResults.find(tr => tr.id === id);
    if (!taskResult) {
      throw { response: { status: 404, data: { error: { message: 'Task result not found' } } } };
    }
    return { data: createResponse(taskResult) };
  }
  
  // List task results with filtering
  let filtered = [...taskResults];
  
  // Filter by patientId (if task results have patientId)
  if (params?.patientId) {
    filtered = filtered.filter(tr => tr.patientId === params.patientId || String(tr.patientId) === String(params.patientId));
  }
  
  // Filter by taskType
  if (params?.taskType) {
    filtered = filtered.filter(tr => tr.taskType === params.taskType);
  }
  
  // Filter by taskName
  if (params?.taskName) {
    filtered = filtered.filter(tr => tr.taskName?.toLowerCase().includes(params.taskName.toLowerCase()));
  }
  
  // Filter by date range
  if (params?.startDate) {
    filtered = filtered.filter(tr => new Date(tr.timestamp) >= new Date(params.startDate));
  }
  if (params?.endDate) {
    filtered = filtered.filter(tr => new Date(tr.timestamp) <= new Date(params.endDate));
  }
  
  // Sort by timestamp (newest first)
  filtered.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  
  return { data: createResponse(filtered) };
}

async function handleTaskResultPost(data: any) {
  const taskResults = storage.get<any[]>('pp_task_results_v1', []);
  
  const newTaskResult = {
    id: `task_result_${Date.now()}`,
    ...data,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
  
  taskResults.push(newTaskResult);
  storage.set('pp_task_results_v1', taskResults);
  
  return { data: createResponse(newTaskResult) };
}

async function handleTaskResultPut(id: string, data: any) {
  const taskResults = storage.get<any[]>('pp_task_results_v1', []);
  const index = taskResults.findIndex(tr => tr.id === id);
  
  if (index === -1) {
    throw { response: { status: 404, data: { error: { message: 'Task result not found' } } } };
  }
  
  taskResults[index] = {
    ...taskResults[index],
    ...data,
    updatedAt: new Date().toISOString(),
  };
  
  storage.set('pp_task_results_v1', taskResults);
  
  return { data: createResponse(taskResults[index]) };
}

async function handleTaskResultDelete(id: string) {
  const taskResults = storage.get<any[]>('pp_task_results_v1', []);
  const filtered = taskResults.filter(tr => tr.id !== id);
  
  if (filtered.length === taskResults.length) {
    throw { response: { status: 404, data: { error: { message: 'Task result not found' } } } };
  }
  
  storage.set('pp_task_results_v1', filtered);
  
  return { data: createResponse({ success: true }) };
}

// ============================================================================
// Encounter Handlers
// ============================================================================

async function handleEncounterGet(id: string | undefined, params?: any) {
  let encounters = storage.get<any[]>('pp_encounters_v1', []);
  
  // Filter by patientId if provided
  if (params?.patientId) {
    encounters = encounters.filter(e => String(e.patientId) === String(params.patientId));
  }
  
  // Filter by status if provided
  if (params?.status) {
    encounters = encounters.filter(e => e.status === params.status);
  }
  
  // Filter by date range if provided
  if (params?.startDate) {
    encounters = encounters.filter(e => new Date(e.start) >= new Date(params.startDate));
  }
  if (params?.endDate) {
    encounters = encounters.filter(e => new Date(e.start) <= new Date(params.endDate));
  }
  
  if (id) {
    const encounter = encounters.find(e => e.id === id);
    if (!encounter) {
      throw { response: { status: 404, data: { error: { message: 'Encounter not found' } } } };
    }
    return { data: createResponse(encounter) };
  }
  
  // Sort by start date (newest first)
  encounters.sort((a, b) => new Date(b.start).getTime() - new Date(a.start).getTime());
  
  return { data: createResponse(encounters) };
}

async function handleEncounterPost(data: any) {
  const encounters = storage.get<any[]>('pp_encounters_v1', []);
  
  const newEncounter = {
    id: `encounter_${Date.now()}`,
    ...data,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
  
  encounters.push(newEncounter);
  storage.set('pp_encounters_v1', encounters);
  
  return { data: createResponse(newEncounter) };
}

async function handleEncounterPut(id: string, data: any) {
  const encounters = storage.get<any[]>('pp_encounters_v1', []);
  const index = encounters.findIndex(e => e.id === id);
  
  if (index === -1) {
    throw { response: { status: 404, data: { error: { message: 'Encounter not found' } } } };
  }
  
  encounters[index] = {
    ...encounters[index],
    ...data,
    updatedAt: new Date().toISOString(),
  };
  
  storage.set('pp_encounters_v1', encounters);
  
  return { data: createResponse(encounters[index]) };
}

async function handleEncounterDelete(id: string) {
  const encounters = storage.get<any[]>('pp_encounters_v1', []);
  const filtered = encounters.filter(e => e.id !== id);
  
  if (filtered.length === encounters.length) {
    throw { response: { status: 404, data: { error: { message: 'Encounter not found' } } } };
  }
  
  storage.set('pp_encounters_v1', filtered);
  
  return { data: createResponse({ success: true }) };
}

// ============================================================================
// Follower Handlers
// ============================================================================

async function handleFollowerGet(id: string | undefined, params?: any) {
  const patientId = params?.patientId;
  
  if (!patientId) {
    throw { response: { status: 400, data: { error: { message: 'patientId is required' } } } };
  }
  
  const key = `patientFollowers_${patientId}`;
  const followersData = storage.get<any>(key, { followers: [] });
  
  if (id) {
    // Get specific follower
    const follower = followersData.followers.find((f: any) => f.id === id);
    if (!follower) {
      throw { response: { status: 404, data: { error: { message: 'Follower not found' } } } };
    }
    return { data: createResponse(follower) };
  }
  
  // Get all followers for patient
  return { data: createResponse(followersData) };
}

async function handleFollowerPost(data: any) {
  const { patientId, ...followerData } = data;
  
  if (!patientId) {
    throw { response: { status: 400, data: { error: { message: 'patientId is required' } } } };
  }
  
  const key = `patientFollowers_${patientId}`;
  const followersData = storage.get<any>(key, { followers: [] });
  
  // Check if follower already exists
  const existingIndex = followersData.followers.findIndex((f: any) => {
    if (followerData.type === 'user' && f.type === 'user' && f.userId === followerData.userId) {
      return true;
    }
    if (followerData.type === 'group' && f.type === 'group' && f.groupId === followerData.groupId) {
      return true;
    }
    return false;
  });
  
  if (existingIndex !== -1) {
    // Update existing follower
    followersData.followers[existingIndex] = {
      ...followersData.followers[existingIndex],
      ...followerData,
      notificationSettings: {
        ...followersData.followers[existingIndex].notificationSettings,
        ...followerData.notificationSettings,
      },
    };
  } else {
    // Add new follower
    const newFollower = {
      id: `follower-${Math.random().toString(36).slice(2, 9)}-${Date.now()}`,
      ...followerData,
      followedAt: new Date().toISOString(),
      followedBy: 'current-user-id', // TODO: Get from auth context
      notificationSettings: {
        alerts: true,
        measurements: true,
        activities: false,
        tasks: true,
        ...followerData.notificationSettings,
      },
    };
    followersData.followers.push(newFollower);
  }
  
  storage.set(key, followersData);
  
  return { data: createResponse(followersData) };
}

async function handleFollowerPut(id: string, data: any) {
  const { patientId, ...updateData } = data;
  
  if (!patientId) {
    throw { response: { status: 400, data: { error: { message: 'patientId is required' } } } };
  }
  
  const key = `patientFollowers_${patientId}`;
  const followersData = storage.get<any>(key, { followers: [] });
  const index = followersData.followers.findIndex((f: any) => f.id === id);
  
  if (index === -1) {
    throw { response: { status: 404, data: { error: { message: 'Follower not found' } } } };
  }
  
  followersData.followers[index] = {
    ...followersData.followers[index],
    ...updateData,
    notificationSettings: {
      ...followersData.followers[index].notificationSettings,
      ...updateData.notificationSettings,
    },
  };
  
  storage.set(key, followersData);
  
  return { data: createResponse(followersData.followers[index]) };
}

async function handleFollowerDelete(id: string) {
  // For delete, we need to find the patientId from the follower ID
  // Since we don't have patientId in the URL, we'll search all patient followers
  // In a real API, the endpoint would be /followers/:patientId/:followerId
  // For now, we'll accept patientId in params or search all
  
  // This is a simplified version - in production, the endpoint should include patientId
  const allKeys = Object.keys(localStorage);
  const followerKeys = allKeys.filter(key => key.startsWith('patientFollowers_'));
  
  for (const key of followerKeys) {
    const followersData = storage.get<any>(key, { followers: [] });
    const index = followersData.followers.findIndex((f: any) => f.id === id);
    
    if (index !== -1) {
      followersData.followers.splice(index, 1);
      storage.set(key, followersData);
      return { data: createResponse({ success: true }) };
    }
  }
  
  throw { response: { status: 404, data: { error: { message: 'Follower not found' } } } };
}

// ============================================================================
// Group Handlers
// ============================================================================

function handleGroupGet(id: string | undefined, params?: any) {
  const groups = storage.get<any[]>('groups', []);
  
  if (id) {
    const group = groups.find(g => g.id === id);
    if (!group) {
      throw { response: { status: 404, data: { error: { message: 'Group not found' } } } };
    }
    return { data: createResponse(group) };
  }
  
  // List groups with pagination and search
  const page = params?.page || 1;
  const limit = params?.limit || 100;
  const search = params?.search?.toLowerCase();
  
  let filtered = groups;
  
  // Filter by search term
  if (search) {
    filtered = filtered.filter(g => 
      g.name?.toLowerCase().includes(search) ||
      g.description?.toLowerCase().includes(search)
    );
  }
  
  const start = (page - 1) * limit;
  const end = start + limit;
  const paginated = filtered.slice(start, end);
  
  return {
    data: createResponse(createPaginatedResponse(paginated, page, limit, filtered.length)),
  };
}

function handleGroupPost(data: any) {
  const groups = storage.get<any[]>('groups', []);
  
  const newGroup = {
    id: `group_${Date.now()}`,
    name: data.name,
    description: data.description || '',
    userIds: data.userIds || [],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
  
  groups.push(newGroup);
  storage.set('groups', groups);
  
  return { data: createResponse(newGroup) };
}

function handleGroupPut(id: string, data: any) {
  const groups = storage.get<any[]>('groups', []);
  const index = groups.findIndex(g => g.id === id);
  
  if (index === -1) {
    throw { response: { status: 404, data: { error: { message: 'Group not found' } } } };
  }
  
  groups[index] = {
    ...groups[index],
    name: data.name !== undefined ? data.name : groups[index].name,
    description: data.description !== undefined ? data.description : groups[index].description,
    userIds: data.userIds !== undefined ? data.userIds : groups[index].userIds,
    updatedAt: new Date().toISOString(),
  };
  
  storage.set('groups', groups);
  
  return { data: createResponse(groups[index]) };
}

function handleGroupDelete(id: string) {
  const groups = storage.get<any[]>('groups', []);
  const filtered = groups.filter(g => g.id !== id);
  
  if (filtered.length === groups.length) {
    throw { response: { status: 404, data: { error: { message: 'Group not found' } } } };
  }
  
  storage.set('groups', filtered);
  
  return { data: createResponse({ success: true }) };
}

